import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";

interface ServiceOption {
  title: string;
  description: string;
  details: string[] | null;
}

const dallasOfferOptions = [
  {
    label: "30% Discount",
    desc: "30% off your final project rate. Includes strategy, sitemap planning, 5–10 custom-designed pages, Framer build, basic CMS setup, and on-site SEO structure.",
  },
  {
    label: "$1,000 One-Pager",
    desc: "A focused, conversion-ready single-page site at a flat rate. Includes structured layout, mobile-optimized Framer build, contact form integration, and basic SEO setup.",
  },
  {
    label: "Barter / Trade",
    desc: "Project exchange in lieu of full payment. Priority given to brands in music, hospitality, food & beverage, events, and photography.",
  },
];

interface StepYourProjectProps {
  projectType: string;
  updateProjectType: (value: string) => void;
  isDallasLocal: boolean;
  setIsDallasLocal: (v: boolean) => void;
  dallasOfferType: string;
  setDallasOfferType: (v: string) => void;
  onViewOfferDetails?: (offerType: string) => void;
}

const StepYourProject = ({
  projectType,
  updateProjectType,
  isDallasLocal,
  setIsDallasLocal,
  dallasOfferType,
  setDallasOfferType,
  onViewOfferDetails,
}: StepYourProjectProps) => {
  const [services, setServices] = useState<ServiceOption[]>([]);

  useEffect(() => {
    const fetchServices = async () => {
      const { data } = await supabase
        .from("services")
        .select("title, description, details")
        .order("display_order", { ascending: true });
      if (data) setServices(data);
    };
    fetchServices();
  }, []);

  return (
    <div className="flex flex-col gap-6 px-6 py-5">
      <div>
        <p className="font-heading text-ts-base text-foreground mb-1">
          Your project
        </p>
        <p className="font-body text-ts-xs text-foreground/60">
          What are you looking for?
        </p>
      </div>

      {/* Service cards */}
      <div className="flex flex-col gap-2">
        <label className="font-body text-ts-2xs font-medium tracking-[0.1em] text-muted-foreground mb-0.5 block">
          Project type
        </label>
        {services.map((service) => {
          const isSelected = projectType === service.title;
          return (
            <button
              key={service.title}
              type="button"
              onClick={() =>
                updateProjectType(isSelected ? "" : service.title)
              }
              className={`w-full text-left p-3 border transition-colors ${
                isSelected
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-foreground/30"
              }`}
            >
              <p className="font-body text-ts-xs font-medium text-foreground">
                {service.title}
              </p>
              <p className="font-body text-[11px] text-foreground/50 mt-0.5 leading-relaxed">
                {service.description}
              </p>

              <AnimatePresence>
                {isSelected && service.details && service.details.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="border-l-2 border-border pl-3 mt-3 py-1">
                      <ul className="space-y-0.5">
                        {service.details.map((detail) => (
                          <li
                            key={detail}
                            className="font-body text-[11px] text-foreground/40 leading-relaxed"
                          >
                            • {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          );
        })}
      </div>

      {/* Dallas Local Toggle */}
      <div className="pt-2">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-body text-ts-xs font-medium text-foreground">
              "Locals Only" Club Member
            </p>
            <p className="font-body text-ts-2xs text-muted-foreground mt-0.5">
              Are you a Dallas native? Unlock our community offers
            </p>
          </div>
          <Switch
            checked={isDallasLocal}
            onCheckedChange={(checked) => {
              setIsDallasLocal(checked);
              if (!checked) setDallasOfferType("");
            }}
          />
        </div>

        <AnimatePresence>
          {isDallasLocal && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="overflow-hidden"
            >
              <div className="pt-4 flex flex-col gap-2">
                <p className="font-body text-ts-2xs text-muted-foreground">
                  Choose your offer:
                </p>
                {dallasOfferOptions.map((opt) => {
                  const isSelected = dallasOfferType === opt.label;
                  return (
                    <div key={opt.label}>
                      <button
                        type="button"
                        onClick={() =>
                          setDallasOfferType(isSelected ? "" : opt.label)
                        }
                        className={`w-full text-left p-3 border transition-colors ${
                          isSelected
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-foreground/30"
                        }`}
                      >
                        <p className="font-body text-ts-2xs font-medium text-foreground">
                          {opt.label}
                        </p>
                        <p className="font-body text-[11px] text-foreground/50 mt-0.5 leading-relaxed">
                          {opt.desc}
                        </p>
                      </button>
                      <AnimatePresence>
                        {isSelected && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                onViewOfferDetails?.(dallasOfferType)
                              }
                              className="mt-1.5 font-body text-ts-2xs font-medium text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
                            >
                              View details
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default StepYourProject;
